<template>
  <div class="grid-content" ref="phonesTable">
    <b-table v-if="columns && columns.length > 0" :data="phonesFiltered" :loading="phones === null" hoverable sticky-header
          paginated backend-pagination :total="phones ? phones.Total : 0" :current-page="page" :per-page="30" @page-change="onPageChange"
          backend-sorting default-sort="CreateDate" :default-sort-direction="'desc'"
          :backend-filtering="true"
          @filters-change="onFiltersChange"
          @sort="onPageSort"
          >
      <b-table-column v-for="column in columns" :key="column.id" :field="column.name" :cell-class="column.class" :label="column.label"
            :visible="column.visible && !column.forbidden && column.id < 1000"
            :width="column.width"
            :sortable="column.sortable" :searchable="column.searchable"
            :header-class="'column-th-' + column.name">
        <template v-if="column.searchable" #searchable="props">
          <b-input v-model="props.filters[props.column.field]" @blur="onColumnFiltersBlur" v-on:keyup.native.enter="onColumnFiltersEnter" />
        </template>
        <template v-slot="props">
          <div v-if="column.name === 'PhoneStatus'">
            <div class="status">
              <template v-if="props.row.StatusID === PhoneStatus.Active">
                <span class="indicator indicator-1"></span>
              </template>
              <template v-else-if="props.row.StatusID === PhoneStatus.InActive">
                <span class="indicator indicator-2"></span>
              </template>
              <span class="text">{{ props.row.PhoneStatus }}</span>
              <!--   
              <b-tooltip v-if="props.row.StatusID === PhoneStatus.InActive" position="is-top" multilined type="is-dark">
              <span class="roi-help">
                <icon-base width="16" height="16" view-box="0 0 16 16" icon-name="chart-pie"><icon-question-circle /></icon-base>
              </span>
              <template #content>
                {{ $t("Companies.CreditCardProblem") }}
              </template>
            </b-tooltip> -->
          </div>
        </div>
        <div v-else-if="column.name === 'PercentageResponse'">
          {{ getPercentageResponse(props.row["NumberOfAnsweredCalls"], props.row["NumberOfMissedCalls"]) }}
        </div>
        <div v-else>{{ getFormattedValue(props.row[column.name], column.name) }}</div>
      </template>
    </b-table-column>
    <b-table-column cell-class="cell-actions" field="actions" :label="$t('Phones.Grid.Columns.Actions')" width="100" :sticky="true" v-slot="props"
          :visible="isActionsColumnVisible">
      <div class="actions-list">
        <b-tooltip :label="$t('Phones.Grid.ActionsColumn.Edit')" position="is-top" type="is-dark"
              v-if="props.row.StatusID !== PhoneStatus.Released">
          <div class="action" data-testid="edit-phone-button" @click="emitTogglePhoneEdit($event, props.row)">
            <icon-base width="18" height="18" view-box="0 0 18 18" icon-name="pencil"><icon-pencil /></icon-base>
          </div>
        </b-tooltip>
        <b-tooltip :label="$t('Phones.Grid.ActionsColumn.History')" position="is-top" type="is-dark">
          <a :href="`/Phones/History?Id=${props.row.Id}`" class="action">
            <icon-base width="22" height="15" view-box="0 0 22 15" icon-name="eye"><icon-eye /></icon-base>
          </a>
          <!-- TODO: uncomment after created History page
          <router-link :to="`/Phones/History?Id=${props.row.Id}`" exact class="action">
            <icon-base width="22" height="15" view-box="0 0 22 15" icon-name="eye"><icon-eye /></icon-base>
          </router-link> -->
          </b-tooltip>
          <b-tooltip class="tooltip-phone" :label="$t('Phones.Grid.ActionsColumn.Release')" position="is-top" type="is-dark">
            <a href="#" class="action action-phone" :class="{ disabled: props.row.StatusID === PhoneStatus.Released }"
                   @click="releasePhone($event, props.row.Id)">
              <icon-base width="17.02" height="20" view-box="0 0 17.02 20" icon-name="mobile-arrow"><icon-mobile-arrow /></icon-base>
            </a>
          </b-tooltip>
        </div>
      </b-table-column>
    </b-table>
  </div>
</template>

<script lang="ts">
import moment from "moment";
import { Component, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import i18n from "@/i18n";
import { RefreshGridMode } from "@/models/leads/Lead";
import { DataSourceResult } from "@/models/DataSourceResult";
import { GridColumn } from "@/models/GridColumn";
import { FilterCollection } from "@/models/FilterCollection";
import { PhoneStatus, PhoneWithChannel } from "@/models/phones/PhoneWithChannel";
import { PhonesPageData } from "@/models/phones/PhonesPageData";
import { Result } from "@/models/Result";
import HelperService from "@/services/HelperService";
import PhonesApiService from "@/services/api/PhonesApiService";
import IconBase from "../IconBase.vue";
import IconEye from "../icons/IconEye.vue";
import IconMobileArrow from "../icons/IconMobileArrow.vue";
import IconPencil from "../icons/IconPencil.vue";
import IconQuestionCircle from "../icons/IconQuestionCircle.vue";

const namespace = "phonesModule";

@Component({
  components: {
    IconBase,
    IconEye,
    IconMobileArrow,
    IconPencil,
    IconQuestionCircle,
  },
})
export default class PhonesGridContent extends Vue {

  //#region Vuex getters and actions

  @Getter("columns", { namespace }) columns!: GridColumn[];
  @Getter("page", { namespace }) page!: number;
  @Getter("phones", { namespace }) phones!: DataSourceResult;
  @Getter("phonesPageData", { namespace }) phonesPageData!: PhonesPageData;
  @Getter("selectedSourcesIds", { namespace }) selectedSourcesIds!: string[];
  @Action("setColumnsFilters", { namespace }) setColumnsFilters!: (columnsFilters: FilterCollection) => void;
  @Action("setNeedToRefreshPhonesGrid", { namespace }) setNeedToRefreshPhonesGrid!: (needToRefreshPhonesGrid: RefreshGridMode) => void;
  @Action("setPage", { namespace }) setPage!: (page: number) => void;
  @Action("setSelectedPhone", { namespace }) setSelectedPhone!: (selectedPhone?: PhoneWithChannel) => void;
  @Action("setSortDir", { namespace }) setSortDir!: (sortDir: string) => void;
  @Action("setSortField", { namespace }) setSortField!: (sortField: string) => void;

  //#endregion Vuex getters and actions
  

  //#region Properties

  isColumnFiltersUpdated = false;
  PhoneStatus = PhoneStatus;

  connectionAction = true;
  phoneOn = true;

  //#endregion Properties


  //#region Getters

  get isActionsColumnVisible(): boolean {
    const column: GridColumn | undefined = this.columns.find((i: GridColumn) => i.name === "Actions");
    if (column) {
      return column.visible;
    } else {
      return false;
    }
  }

  get phonesFiltered(): PhoneWithChannel[] {
    if (!this.phones?.Data) {
      return [];
    }
    if (this.selectedSourcesIds.length === 0) {
      return this.phones.Data as PhoneWithChannel[];
    } else {
      return (this.phones.Data as PhoneWithChannel[]).filter((i: PhoneWithChannel) =>
            i.ChannelId && this.selectedSourcesIds.indexOf(i.ChannelId.toString()) >= 0
              || i.AssetId && this.selectedSourcesIds.indexOf(`${ i.AssetId }:${ i.AssetTypeId }`) >= 0);
    }
  }

  //#endregion Getters


  //#region Handlers

  emitTogglePhoneEdit(e: Event, phone: PhoneWithChannel): void {
    e.preventDefault();
    this.setSelectedPhone(phone);
    this.$emit("toggle-phone-edit");
  }

  onColumnFiltersBlur(e: Event) {
    if (this.isColumnFiltersUpdated) {
      this.isColumnFiltersUpdated = false;
      this.setNeedToRefreshPhonesGrid(RefreshGridMode.Full);
    }
  }

  onColumnFiltersEnter(e: Event) {
    (e.target as HTMLInputElement).blur();
  }

  onFiltersChange(filterObject: any) {
    const filterCollection: FilterCollection = {
      logic: "and",
      filters: [],
    };
    for (const key of Object.keys(filterObject)) {
      const val: any = filterObject[key];
      if (val) {
        filterCollection.filters?.push({ field: key, operator: "contains", value: `'${val}'` });
      }
    }
    this.setColumnsFilters(filterCollection);
    this.isColumnFiltersUpdated = true;
  }

  onPageChange(page: number) {
    this.setPage(page);
    this.setNeedToRefreshPhonesGrid(RefreshGridMode.GridOnly);
  }

  onPageSort(fieldName: string, sortDirection: string, e: PointerEvent) {
    this.setSortDir(sortDirection);
    this.setSortField(fieldName);
    this.setNeedToRefreshPhonesGrid(RefreshGridMode.GridOnly);
  }

  releasePhone(e: Event, id: number): void {
    e.preventDefault();
    HelperService.showConfirmDialog(
      undefined,
      i18n.t("Phones.Grid.ReleasePhonePopup.Message").toString(),
      i18n.t("OK").toString(),
      i18n.t("Cancel").toString(),
      () => {
        let loader = this.$loading.show({
          container: this.$refs.phonesTable
        });
        PhonesApiService.releasePhone(id, this.phonesPageData.CompanyId).then((result: Result) => {
          if (result.Success) {
            this.setNeedToRefreshPhonesGrid(RefreshGridMode.Full);
          } else {
            HelperService.showError(result.Message);
          }
        }).finally(() => {
          loader.hide();
        });
      }
    );
  }

  //#endregion Handlers


  //#region Private methods

  getFormattedValue(value: number | string | Date, fieldName: string): string {
    if (value === null) {
      return "";
    }
    if (fieldName === "CreateDate") {
      return moment(value as Date).format("DD/MM/yyyy HH:mm");
    } else {
      return value as string;
    }
  }

  getPercentageResponse(answeredCallsCount?: number, notAnsweredCallsCount?: number): string {
    let percentage = 0;
    if (!answeredCallsCount) {
      answeredCallsCount = 0;
    }
    if (!notAnsweredCallsCount) {
      notAnsweredCallsCount = 0;
    }
    const sum: number = answeredCallsCount + notAnsweredCallsCount;
    if (sum === 0) {
      return "";
    } else {
      percentage = answeredCallsCount / (answeredCallsCount + notAnsweredCallsCount) * 100;
    }
    return `${parseFloat(percentage.toFixed(2))}%`;
  }

  //#endregion Private methods
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.grid-content {
  .b-table {
    .table {
      thead {
        th {
          &.column-th-AverageCallSeconds {
            min-width: 140px;
          }

          &.column-th-CallsInLast30Days {
            min-width: 200px;
          }

          &.column-th-ChannelMetadataType {
            min-width: 160px;
          }

          &.column-th-ChannelName {
            min-width: 300px;
          }

          &.column-th-CompanyTitle {
            min-width: 260px;
          }

          &.column-th-CreateDate {
            min-width: 160px;
          }

          &.column-th-Id {
            min-width: 120px;
          }

          &.column-th-NumberOfAnsweredCalls {
            min-width: 160px;
          }

          &.column-th-NumberOfMissedCalls {
            min-width: 140px;
          }

          &.column-th-PercentageResponse {
            min-width: 210px;
          }

          &.column-th-PhoneNumber {
            min-width: 160px;
          }

          &.column-th-PhoneStatus {
            min-width: 100px;
          }

          &.column-th-RedirectPhone {
            min-width: 160px;
          }

          &.column-th-TotalCallSeconds {
            min-width: 126px;
          }
        }
      }
    }
  }

  .table thead th,
  .table tbody td {
    &.cell-phone,
    &.cell-receive-call,
    &.cell-company,
    &.cell-date,
    &.cell-calls-number,
    &.cell-advertising-channel,
    &.cell-channel-name,
    &.cell-project,
    &.cell-answered-calls,
    &.cell-missed-calls,
    &.cell-percentage-response,
    &.cell-average-call,
    &.cell-status {
      white-space: nowrap;
      @media (max-width: 479px) {
        white-space: normal;
      }
    }

    &.cell-status {
      .status {
        display: flex;
        align-items: center;

        .b-tooltip {
          margin-inline-start: .3rem;
        }

        .indicator {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          border: 1.5px solid #aaa;
          margin-inline-end: 5px;

          &.indicator-1 {
            background-color: $success;
            background: $success;
          }
          &.indicator-2 {
            background: $danger;
            border-color: $danger;
          }
        }
      }
    }
  }

  .actions-list {
    .action {
      &.action-notifications {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        .icon-w {
          line-height: 0;
        }
        .tag.is-small {
          position: absolute;
          left: 50%;
          margin-left: 1px;
          top: -11px;
          height: 19px;
          line-height: 12px;
          border: 2px solid #fff;
          border-radius: 4px;
          padding: 0;
          width: auto;
          .tag-i {
            border: 1px solid $danger;
            background: #fff;
            color: $danger;
            border-radius: 2px;
            padding: 0 2px;
            font-weight: 600;
          }
        }
      }
      &.action-phone {
        .icon-mobile-arrow {
          color: $danger;
        }

        &.disabled {
          .icon-mobile-arrow {
            color: $disabled;
          }
        }
      }
    }
    .tooltip-phone {
      .tooltip-content {
        transform: translateX(-20%);
        &:before {
          left: 20%;
        }
      }
    }
  }
}

html[dir="ltr"] {
  .grid-content {
    .actions-list {
      .tooltip-phone {
        .tooltip-content {
          transform: translateX(-80%);
          &:before {
            left: 80%;
          }
        }
      }
    }
    .table thead th,
    .table tbody td {
      &.cell-channel-name {
        .channel-name-title {
          @media (max-width: 768px) {
            text-align: right !important;
          }
        }
        .channel-name-info {
          .channel-name-status {
            .indicator {
              margin: 0 5px 0 0;
              @media (max-width: 768px) {
                margin: 0 0 0 5px;
              }
            }
          }
        }
        .channel-name-logo {
          margin: 0 10px 0 0;
          @media (max-width: 768px) {
            margin: 0 0 0 10px;
          }
        }
      }
      &.cell-ad-preview-2 {
        @media (max-width: 768px) {
          text-align: left !important;
        }
        .channel-name-img {
          margin: 0 20px 0 0;
          @media (max-width: 768px) {
            margin: 0 10px 0 0;
          }
        }
        .channel-name-info {
          .channel-name-status {
            .indicator {
              margin: 0 5px 0 0;
            }
          }
        }
      }
    }
  }
}
</style>
